/**
 * Read-only availability calendar for the BookingWidget (Iter 67e).
 *
 * Replaces the previous text list of "UNAVAILABLE" date ranges with a single-
 * month mini-calendar that highlights:
 *   - Confirmed / pending_payment bookings  → rose (already-booked).
 *   - Vendor-defined unavailable windows    → amber (vendor blocked).
 * Past days are dimmed so the renter sees only "what could I rent".
 *
 * Renders nothing when there are no blocked ranges — the widget stays clean for
 * cars with wide-open calendars.
 */
import { Calendar } from "@/components/ui/calendar";
import { fromLocalIso, localToday } from "@/lib/localDate";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { CalendarDays, ChevronDown } from "lucide-react";

// Build a Set of LOCAL-midnight timestamps to check membership without re-parsing
// the range list on every cell render. End dates are EXCLUSIVE — a range
// {start: 2026-03-15, end: 2026-03-18} blocks 15, 16, 17 but NOT 18.
function _explodeRanges(ranges, statusFilter) {
  const out = new Set();
  for (const r of ranges || []) {
    if (statusFilter && !statusFilter(r)) continue;
    const s = fromLocalIso(r.start_date);
    const e = fromLocalIso(r.end_date);
    if (!s || !e) continue;
    const cur = new Date(s);
    while (cur < e) {
      out.add(cur.getTime());
      cur.setDate(cur.getDate() + 1);
    }
  }
  return out;
}

export default function BookingAvailabilityCalendar({ bookedRanges = [] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const today = localToday();
  // Hooks must run unconditionally — even if `bookedRanges` is empty we still
  // call useMemo, then bail out below. Otherwise the call order shifts between
  // renders and React throws "called conditionally".
  const { bookedDays, blockedDays, defaultMonth, futureCount } = useMemo(() => {
    const booked = _explodeRanges(bookedRanges, (r) => r.status !== "unavailable");
    const blocked = _explodeRanges(bookedRanges, (r) => r.status === "unavailable");
    const earliest = bookedRanges
      .map((r) => fromLocalIso(r.start_date)?.getTime())
      .filter((ts) => typeof ts === "number" && ts >= today.getTime())
      .sort((a, b) => a - b)[0];
    return {
      bookedDays: booked,
      blockedDays: blocked,
      defaultMonth: earliest ? new Date(earliest) : today,
      futureCount: bookedRanges.filter((r) => {
        const e = fromLocalIso(r.end_date)?.getTime();
        return typeof e === "number" && e >= today.getTime();
      }).length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookedRanges]);

  if (!bookedRanges.length) return null;

  return (
    <div
      className="rounded-2xl border border-border bg-white mb-3 overflow-hidden"
      data-testid="availability-calendar"
    >
      {/* Collapsible header: a single button so keyboard users can toggle the
          calendar with Enter/Space. Closed by default — keeps the widget compact. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left transition ${
          open ? "bg-rose-50/40" : "hover:bg-neutral-50"
        }`}
        data-testid="availability-calendar-toggle"
      >
        <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-700">
          <CalendarDays className="h-3.5 w-3.5 text-rose-500" />
          {t("bookingWidget.unavailable", "Availability")}
          {futureCount > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold">
              {futureCount}
            </span>
          )}
        </span>
        <ChevronDown className={`h-4 w-4 text-neutral-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-2 pb-2" data-testid="availability-calendar-body">
          <Calendar
            mode="single"
            defaultMonth={defaultMonth}
            modifiers={{
              booked: (d) => bookedDays.has(d.getTime()),
              blocked: (d) => blockedDays.has(d.getTime()),
              past: (d) => d < today,
            }}
            modifiersClassNames={{
              booked: "bg-rose-100 text-rose-700 font-semibold line-through opacity-90 hover:bg-rose-100",
              blocked: "bg-amber-100 text-amber-800 font-semibold opacity-90 hover:bg-amber-100",
              past: "text-neutral-300",
            }}
            disabled={(d) => bookedDays.has(d.getTime()) || blockedDays.has(d.getTime()) || d < today}
            showOutsideDays={false}
            className="p-2"
          />
          <div className="flex items-center justify-center gap-3 pt-1 pb-1.5 text-[10px] text-neutral-600">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-rose-200 ring-1 ring-rose-300" />
              Booked
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-amber-200 ring-1 ring-amber-300" />
              Blocked
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
